import { Authenticator, Theme, ThemeProvider } from "@aws-amplify/ui-react";
// Dev Note: Would you just look at the locations of these convenient type definitions?
// And they have to be tracked down by hand! Just lovely...
import { DefaultComponents } from "@aws-amplify/ui-react/dist/types/components/Authenticator/hooks/useCustomComponents/defaultComponents";
import { AuthFormFields } from "@aws-amplify/ui/dist/types/types/authenticator/form";
import { Auth, I18n } from "aws-amplify";

import { Header } from "./features/login/Header";
import { SignInHeader } from "./features/login/SignInHeader";
import { SignInFooter } from "./features/login/SignInFooter";
import { DefaultHome } from "./DefaultHome";
import "./features/login/styles.css";
import { SIMPLColors } from "./theme";
import { ResetPasswordHeader } from "./features/login/ResetPasswordHeader";
import { ResetPasswordFooter } from "./features/login/ResetPasswordFooter";
import { ConfirmResetPasswordHeader } from "./features/login/ConfirmResetPasswordFooter";
import { ConfirmResetPasswordFooter } from "./features/login/ConfirmResetPasswordHeader";
import { InvitationFormErrors } from "./features/UserInvitations/userInvitationUtils";
import { handleForgotPassword } from "./features/login/forgotPasswordUtils";
import { ConfirmSignUpHeader } from "./features/login/ConfirmSignUpHeader";
import { useNavigate } from "react-router-dom";
import { Footer } from "./features/login/Footer";
import { Mixpanel } from "./features/Analytics/Mixpanel";
import { MixpanelEvents } from "./app/constants";
import { DebugEnvInfo } from "./components/DebugEnvInfo";

// Set to false to disable cognito login in your environment
const ENABLE_AUTH = true;

I18n.putVocabulariesForLanguage("en", {
  "Incorrect username or password.":
    "Email address and password combination is not recognized. Try again.",
  "Password does not conform to policy: Password must have lowercase characters":
    InvitationFormErrors.PasswordRequirementsNotMet,
  "Password does not conform to policy: Password must have uppercase characters":
    InvitationFormErrors.PasswordRequirementsNotMet,
  "Password does not conform to policy: Password must have numeric characters":
    InvitationFormErrors.PasswordRequirementsNotMet,
  "Password does not conform to policy: Password must have symbol characters":
    InvitationFormErrors.PasswordRequirementsNotMet
});

export const authenticatorComponents: DefaultComponents = {
  Header,
  SignIn: {
    Header: SignInHeader,
    Footer: SignInFooter
  },
  // Hiding the footer for now to take away the "register here" link.
  Footer: Footer,
  ResetPassword: {
    Header: ResetPasswordHeader,
    Footer: ResetPasswordFooter
  },
  ConfirmResetPassword: {
    Header: ConfirmResetPasswordHeader,
    Footer: ConfirmResetPasswordFooter
  },
  ConfirmSignUp: {
    Header: ConfirmSignUpHeader
  }
};

const formFields: AuthFormFields = {
  signIn: {
    username: {
      placeholder: "Email",
      label: "",
      isRequired: true,
      type: "email"
    },
    password: {
      placeholder: "Password",
      label: "",
      isRequired: true
    }
  },
  resetPassword: {
    username: {
      placeholder: "Enter your email",
      label: "",
      isRequired: true,
      type: "email"
    }
  },
  confirmResetPassword: {
    confirmation_code: {
      placeholder: "Code",
      label: "",
      isRequired: true
    },
    password: {
      placeholder: "New Password",
      label: "",
      isRequired: true
    },
    confirm_password: {
      placeholder: "Confirm Password",
      label: "",
      isRequired: true
    }
  }
};

export const AuthenticatedApp = () => {
  const authTheme: Theme = {
    name: "Auth Theme",
    tokens: {
      fonts: {
        default: {
          variable: { value: "Roboto, sans-serif" },
          static: { value: "Roboto, sans-serif" }
        }
      },
      colors: {
        font: {
          interactive: {
            value: SIMPLColors.Primary
          },
          active: {
            value: SIMPLColors.Secondary
          },
          hover: {
            value: SIMPLColors.Secondary
          },
          focus: {
            value: SIMPLColors.Secondary
          }
        }
      },
      components: {
        button: {
          primary: {
            backgroundColor: {
              value: SIMPLColors.Primary
            },
            _hover: {
              backgroundColor: SIMPLColors.Secondary
            },
            _focus: {
              backgroundColor: SIMPLColors.Secondary
            },
            _active: {
              backgroundColor: SIMPLColors.Secondary
            }
          }
        }
      }
    }
  };

  const navigate = useNavigate();

  const handleNotStarted = () => {
    return;
  };

  const handleNotConfirmed = (username: string) => {
    return;
  };

  const services = {
    async handleSignIn(formData: any) {
      const { username, password } = formData;
      Mixpanel.track(MixpanelEvents.SignInSelected);
      try {
        return await Auth.signIn(username, password);
      } catch (error) {
        const anyError = error as any;
        
        // Log generic login failure without revealing account existence or status
        console.error("Login attempt failed");
        
        // No database calls for unauthenticated users
        // Handle errors without making backend calls
        if (anyError.name === "UserNotFoundException") {
          handleNotStarted();
        } else if (anyError.name === "UserNotConfirmedException") {
          // Users are already confirmed when invited - this shouldn't normally happen
          // If it does, user should contact administrator or use invitation link
          handleNotConfirmed(username);
        }
        
        throw anyError;
      }
    },
    async handleForgotPassword(username: any) {
      // Use centralized forgot password utility - no database lookups
      return await handleForgotPassword(username);
    }
  };

  return (
    <ThemeProvider theme={authTheme}>
      <DebugEnvInfo />
      <Authenticator
        components={authenticatorComponents}
        hideSignUp={true}
        formFields={formFields}
        services={services}
        className="Authenticator"
      >
        <DefaultHome />
      </Authenticator>
    </ThemeProvider>
  );
};

export const NonAuthenticatedApp = () => <DefaultHome />;

export default ENABLE_AUTH ? AuthenticatedApp : NonAuthenticatedApp;
